import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  deleteArticleAsync,
  retrieveAdminCourseById,
  retrieveAdminArticles,
  retrieveAdminCourseUsers,
  retrieveStudentsSolutionInCourseForFreeResponse,
  retrieveStudentsSolutionInCourseForMcq,
  sendAttachedEmail,
  updateUserCertificateStatus,
  retrieveAdminCourseUserInteractionById,
  addNewUserToCourse,
  removeUserFromCourse,
} from "../services/adminCourseServices";
import {
  addUsersToInviteList,
  resetUserToInviteList,
  setAdminArticleStatus,
} from "../features/adminArticleSlice";
import {
  AddMultipleUserListItemType,
  AddMultipleUsersToCoursePayloadType,
  AddUserToCoursePayloadType,
  ApiResponseType,
  CourseUsersType,
  RemoveUserFromCoursePayloadType,
} from "../types/adminCourseTypes";
import { ArticleType, DataStatus } from "../types/LearnTypes";
import { handleSnackbarOpen } from "../features/snackbarSlice";
const dispatchArticleLoadFailure = (
  dispatch: any,
  articleType: ArticleType
) => {
  dispatch(
    setAdminArticleStatus({
      articleType: articleType,
      status: DataStatus.failed,
    })
  );
};

export const retrieveAdminArticlesAction = createAsyncThunk(
  "adminArticle/getCourses",
  async (
    {
      pageNumber,
      resultsPerPage,
      articleType,
      search,
      filter,
      sortBy,
    }: {
      pageNumber: number;
      resultsPerPage: number;
      articleType: ArticleType;
      search?: string;
      filter?: string;
      sortBy?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await retrieveAdminArticles(
        pageNumber,
        resultsPerPage,
        articleType,
        search,
        filter,
        sortBy
      );
      if (response.data && response.status === 200) {
        return thunkAPI.fulfillWithValue({ data: response.data, articleType });
      }
      dispatchArticleLoadFailure(thunkAPI.dispatch, articleType);
      return thunkAPI.rejectWithValue({
        message: "failed to get admin articles",
        articleType,
      });
    } catch (error: any) {
      dispatchArticleLoadFailure(thunkAPI.dispatch, articleType);
      return thunkAPI.rejectWithValue({
        message: "Failed to get admin articles",
        articleType,
      });
    }
  }
);

export const retrieveStudentsSolutionInCourseForMcqAction = createAsyncThunk(
  "adminArticle/getUserMcqSolutionsForCourse",
  async (
    {
      userId,
      courseId,
      pageNumber,
      resultsPerPage,
    }: {
      userId: string;
      courseId: string;
      pageNumber: number;
      resultsPerPage: number;
    },
    thunkAPI
  ) => {
    try {
      const response = await retrieveStudentsSolutionInCourseForMcq(
        userId,
        courseId,
        pageNumber,
        resultsPerPage
      );
      if (response.data && response.status === 200) {
        return thunkAPI.fulfillWithValue({
          ...response.data,
          courseId,
          userId,
        });
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to get users response for mcq in course",
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        message: "failed to get users response for mcq in course",
      });
    }
  }
);

export const retrieveStudentsSolutionInCourseForFreeResponseAction =
  createAsyncThunk(
    "adminArticle/getUserFrqSolutionsForCourse",
    async (
      {
        userId,
        courseId,
        pageNumber,
        resultsPerPage,
      }: {
        userId: string;
        courseId: string;
        pageNumber: number;
        resultsPerPage: number;
      },
      thunkAPI
    ) => {
      try {
        const response = await retrieveStudentsSolutionInCourseForFreeResponse(
          userId,
          courseId,
          pageNumber,
          resultsPerPage
        );
        if (response.data && response.status === 200) {
          return thunkAPI.fulfillWithValue(response.data);
        }
        return thunkAPI.rejectWithValue({
          message: "Failed to get users response for frq in course",
        });
      } catch (error: any) {
        return thunkAPI.rejectWithValue({
          message: "failed to get users response for frq in course",
        });
      }
    }
  );

export const loadCourseById = createAsyncThunk(
  "adminArticle/retrieveAdminCourseById",
  async (
    {
      courseId,
    }: {
      courseId: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await retrieveAdminCourseById(courseId);
      if (response.data && response.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to load course",
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        message: "Failed to load course",
      });
    }
  }
);

export const loadCourseUsersAction = createAsyncThunk(
  "adminArticle/retrieveAdminCourseUsers",
  async (
    {
      courseId,
      page,
      resultsPerPage,
      userRole,
      searchString,
    }: {
      courseId: string;
      page: number;
      resultsPerPage: number;
      userRole: CourseUsersType;
      searchString?: string;
    },
    thunkAPI
  ) => {
    try {
      const response = await retrieveAdminCourseUsers(
        courseId,
        page,
        resultsPerPage,
        userRole,
        searchString
      );
      if (response.data && response.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to load course users",
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        message: "Failed to load course users",
      });
    } finally {
    }
  }
);

export const loadUserCourseInteractionActionById = createAsyncThunk(
  "adminArticle/retrieveAdminUserCourseInteraction",
  async (
    {
      courseId,
      userCourseInteractionId,
      userRole,
    }: {
      courseId: string;
      userCourseInteractionId: string;
      userRole: CourseUsersType;
    },
    thunkAPI
  ) => {
    try {
      const response = await retrieveAdminCourseUserInteractionById(
        courseId,
        userCourseInteractionId,
        userRole
      );
      if (response.data && response.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to load course users",
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        message: "Failed to load course users",
      });
    } finally {
    }
  }
);

export const sendAttachedEmailAction = createAsyncThunk(
  "adminArticle/sendAttachedEmail",
  async (
    {
      to,
      message,
      subject,
      base64Data,
    }: { to: string; message: string; subject: string; base64Data?: string },
    thunkAPI
  ) => {
    try {
      const response = await sendAttachedEmail(
        to,
        message,
        subject,
        base64Data
      );
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err;
    }
  }
);

export const updateUserCertificateAction = createAsyncThunk(
  "adminArticle/updateUserCertificateStatus",
  async (
    {
      userId,
      courseId,
      status,
    }: { userId: string; courseId: string; status: boolean },
    thunkAPI
  ) => {
    try {
      const response = await updateUserCertificateStatus(
        userId,
        courseId,
        status
      );
      if (response.status === 200) {
        return response;
      }
    } catch (err) {
      return err;
    }
  }
);

export const deleteArticleAction = createAsyncThunk(
  "adminArticle/delete",
  async (
    { articleId, articleType }: { articleId: string; articleType: ArticleType },
    thunkAPI
  ) => {
    try {
      const response = await deleteArticleAsync(articleId, articleType);
      if (response.status === 204) {
        return thunkAPI.fulfillWithValue({
          ...response,
          deletedArticleId: articleId,
          articleType,
        });
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to delete the course",
      });
    } catch (err) {
      return thunkAPI.rejectWithValue(err);
    }
  }
);

export const addNewUserToCourseAction = createAsyncThunk(
  "adminArticle/addUserToCourse",
  async (
    { courseId, newUserEmail, userRole }: AddUserToCoursePayloadType,
    thunkAPI
  ) => {
    try {
      const response = await addNewUserToCourse({
        courseId,
        newUserEmail,
        userRole,
      });
      if (response.status === 200) {
        await thunkAPI.dispatch(
          loadCourseUsersAction({
            courseId,
            page: 0,
            resultsPerPage: 10,
            userRole: userRole,
            searchString: "",
          })
        );
        return thunkAPI.fulfillWithValue({ ...response.data, success: true });
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to add users to course.",
        success: false,
      });
    } catch (error: any) {
      if (error?.response?.status) {
        thunkAPI.dispatch(
          handleSnackbarOpen({
            open: true,
            severity: "error",
            message:
              error?.response?.data?.message ||
              "Something went wrong, unable to add new user please try again",
          })
        );
      }
      return thunkAPI.rejectWithValue({
        message: error?.response?.data?.message || "Failed to add new user",
        success: false,
      });
    }
  }
);

export const removeUserFromCourseAction = createAsyncThunk(
  "adminArticle/removeUserFromCourse",
  async (
    {
      courseId,
      removingUserId,
      userRole,
      page = 0,
      resultsPerPage = 10,
    }: RemoveUserFromCoursePayloadType & {
      userRole: CourseUsersType;
      page: number;
      resultsPerPage: number;
    },
    thunkAPI
  ) => {
    try {
      const response = await removeUserFromCourse({ courseId, removingUserId });
      if (response.status === 204) {
        await thunkAPI.dispatch(
          loadCourseUsersAction({
            courseId,
            page: page,
            resultsPerPage: resultsPerPage,
            userRole: userRole,
            searchString: "",
          })
        );
        return thunkAPI.fulfillWithValue({ ...response.data, success: true });
      }
      return thunkAPI.rejectWithValue({
        message: "Failed to remove user",
        success: false,
      });
    } catch (error: any) {
      if (error?.response?.status) {
        thunkAPI.dispatch(
          handleSnackbarOpen({
            open: true,
            severity: "error",
            message:
              error?.response?.data?.message ||
              "Something went wrong, unable to remove user, please try again",
          })
        );
      }
      return thunkAPI.rejectWithValue({
        message: error.message || "Change course lock failed.",
        success: false,
      });
    }
  }
);

export const inviteUserListToCourses = createAsyncThunk(
  "adminArticle/inviteUserListToCourses",
  async (
    {
      usersList,
      courseId,
      callBackFunctionAfterSuccess = () => {},
    }: AddMultipleUsersToCoursePayloadType,
    thunkAPI
  ) => {
    try {
      usersList.map((usr: AddMultipleUserListItemType) => {
        thunkAPI.dispatch(
          addUsersToInviteList({
            id: usr.id,
            email: usr.email,
            role: usr.role,
            status: ApiResponseType.pending,
          })
        );
        return usr;
      });

      const addUserPromises = usersList.map((usr) => {
        thunkAPI.dispatch(
          addUsersToInviteList({
            id: usr.id,
            email: usr.email,
            role: usr.role,
            status: ApiResponseType.loading,
          })
        );
        return addNewUserToCourse({
          courseId,
          newUserEmail: usr.email,
          userRole: usr.role,
        })
          .then((res) => {
            if (res.status === 200) {
              thunkAPI.dispatch(
                handleSnackbarOpen({
                  open: true,
                  severity: "info",
                  message: "User added successfully",
                })
              );
              thunkAPI.dispatch(
                addUsersToInviteList({
                  id: usr.id,
                  email: usr.email,
                  role: usr.role,
                  status: ApiResponseType.success,
                })
              );
            } else {
              thunkAPI.dispatch(
                addUsersToInviteList({
                  id: usr.id,
                  email: usr.email,
                  role: usr.role,
                  status: ApiResponseType.failure,
                  errorMessage:
                    res?.data?.message || "Failed to add user to course",
                })
              );
            }
            return res;
          })
          .catch((err: any) => {
            console.error("failed to add user", err?.response?.data?.message);
            thunkAPI.dispatch(
              handleSnackbarOpen({
                open: true,
                severity: "error",
                message: err?.response?.data?.message || "Failed to add user",
              })
            );
            thunkAPI.dispatch(
              addUsersToInviteList({
                id: usr.id,
                email: usr.email,
                role: usr.role,
                status: ApiResponseType.failure,
                errorMessage:
                  err?.response?.data?.message || "Failed to add user",
              })
            );
          });
      });

      return Promise.allSettled(addUserPromises)
        .then((results) => {
          results.forEach((result) => {
            if (result.status === "rejected") {
              console.log("Failed to add user:", result.reason);
            }
          });
          setTimeout(async () => {
            await thunkAPI.dispatch(
              loadCourseUsersAction({
                courseId,
                page: 0,
                resultsPerPage: 10,
                userRole: CourseUsersType.Student, // loading students
                searchString: "",
              })
            );
            thunkAPI.dispatch(resetUserToInviteList());
            callBackFunctionAfterSuccess();
          }, 5000);
        })
        .catch((err) => {
          console.log("Error adding users to organization: ", err);
        });
    } catch (err: any) {
      thunkAPI.dispatch(
        handleSnackbarOpen({
          open: true,
          message: err?.message || "Failed to add user to course",
          severity: "error",
        })
      );
    }
  }
);
